import { For, JSX, Show, batch, createMemo } from 'solid-js';
import { createStore } from 'solid-js/store';
import { useLogger } from '@trimble/solidjs-utils';
import { t } from '@trimble/i18n';
import {
  GeospatialFeature,
  PropertyDefinition,
  PropertyValue,
} from '@trimble/geospatial';

import { BtnClasses, IconButton, PanelLayout } from '@tcweb/connect-ui';

import { useFeatureExtractionContext } from '@mobile-mapping/providers';
import { PropertyField } from './PropertyField';

const JSON_INDENT = 2;

type PropertyValues = Record<string, PropertyValue | undefined>;

function isPropertyValue(value: unknown): value is PropertyValue {
  return (
    value === null ||
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  );
}

function toPropertyValues(
  properties: Readonly<Record<string, unknown>>
): PropertyValues {
  return Object.fromEntries(
    Object.entries(properties).filter(([, value]) => isPropertyValue(value))
  ) as PropertyValues;
}

export function FeaturePropertiesPanel(props: {
  onClose(): void;
}): JSX.Element {
  const featureExtraction = useFeatureExtractionContext();

  return (
    <PanelLayout
      panelTitle={t('mobileMapping.cadEditing.propertiesPanel.title')}
      titleDataCy="feature-properties-tab-title"
      rightActions={
        <IconButton
          color={BtnClasses.TERTIARY}
          icon="tc-icon-content-close"
          size="icon-medium"
          data-cy="feature-properties-tab-close"
          clicked={() => props.onClose()}
        />
      }
      content={
        <div
          class="feature-extraction-properties flex flex-col gap-4 p-3"
          data-testid="feature-extraction-properties"
        >
          <Show
            when={featureExtraction.editingState.selectedFeature}
            keyed
            fallback={
              <div class="text-sm">
                {t(
                  'mobileMapping.cadEditing.propertiesPanel.noFeatureSelected'
                )}
              </div>
            }
          >
            {(feature) => <FeatureProperties feature={feature} />}
          </Show>
        </div>
      }
    />
  );
}

/** Keyed remount on feature change reseeds drafts (no prop->signal sync). */
function FeatureProperties(props: { feature: GeospatialFeature }): JSX.Element {
  const featureExtraction = useFeatureExtractionContext();
  const logger = useLogger()
    .getLogger('FeatureExtraction')
    .createNamedLogger('PropertiesPanel');

  // The feature's own layer, not the currently-selected layer in the tree —
  // those diverge once another layer is picked for drawing while a feature
  // from a different layer stays selected.
  const featureLayer = createMemo(() =>
    featureExtraction.editingState.layers.find(
      (layer) => layer.id === props.feature.layerId
    )
  );

  const properties = createMemo<PropertyDefinition[]>(
    () => featureLayer()?.template?.properties.slice() ?? []
  );

  const [values, setValues] = createStore<PropertyValues>(
    // eslint-disable-next-line solid/reactivity -- intentional one-time seed; the keyed parent remounts on feature change
    toPropertyValues(props.feature.properties)
  );

  const commit = async (name: string, value: PropertyValue): Promise<void> => {
    const previousValue = values[name];
    setValues(name, value);
    const feature = props.feature;
    const featureId = feature.id;
    const scope = featureExtraction.scope();
    if (!featureId || !scope) {
      setValues(name, previousValue);
      return;
    }
    const featureRepo = featureExtraction.featureRepository();
    if (!featureRepo) {
      setValues(name, previousValue);
      return;
    }
    logger.info('Persisting property change', { id: featureId, name });
    try {
      await featureRepo.updateFeatureProperties(
        scope,
        featureId,
        feature.layerId,
        { [name]: value },
        featureExtraction.pointCloudCrs()
      );
      batch(() => {
        featureExtraction.mergeFeatureProperties(featureId, { [name]: value });
        featureExtraction.bumpTileCacheToken();
      });
    } catch (error) {
      logger.error(
        'Failed to persist property change',
        { id: featureId, name },
        error instanceof Error ? error : new Error(String(error))
      );
      setValues(name, previousValue);
    }
  };

  return (
    <>
      <section class="flex flex-col gap-1">
        <h4 class="text-sm font-semibold">
          {t('mobileMapping.cadEditing.propertiesPanel.generalSection')}
        </h4>
        <div class="text-sm">
          {t('mobileMapping.cadEditing.propertiesPanel.featureId', {
            id: props.feature.id ?? '(unsaved)',
          })}
        </div>
        <div class="text-sm">
          {t('mobileMapping.cadEditing.propertiesPanel.layer', {
            name: featureLayer()?.name ?? props.feature.layerId,
          })}
        </div>
      </section>

      <section class="flex flex-col gap-2">
        <h4 class="text-sm font-semibold">
          {t('mobileMapping.cadEditing.propertiesPanel.propertiesSection')}
        </h4>
        <Show
          when={properties().length > 0}
          fallback={
            <div class="text-sm">
              {t('mobileMapping.cadEditing.propertiesPanel.noSchemaFields')}
            </div>
          }
        >
          <For each={properties()}>
            {(property) => (
              <PropertyField
                property={property}
                value={values[property.name]}
                onCommit={(value) => void commit(property.name, value)}
              />
            )}
          </For>
        </Show>
      </section>

      <section class="flex flex-col gap-1">
        <h4 class="text-sm font-semibold">
          {t('mobileMapping.cadEditing.propertiesPanel.debugMetadataSection')}
        </h4>
        <pre class="max-h-[200px] overflow-auto whitespace-pre-wrap text-xs">
          {JSON.stringify(props.feature.metadata, null, JSON_INDENT)}
        </pre>
      </section>
    </>
  );
}
